// Clara360 Messdienst Dashboard - Vollständig integriert
// Mit UniversalMeterService und echten Supabase-Daten

import React, { useState, useEffect } from 'react';
import { 
  Activity, CheckCircle, TrendingUp, AlertTriangle, 
  Plus, Upload, Settings, RefreshCw, Grid3X3, List,
  Zap, Droplets, Thermometer, Flame, BarChart3,
  Calendar, MapPin, User, Phone, Mail, ExternalLink,
  Clock, AlertCircle, CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import UniversalMeterService from '../../services/UniversalMeterService';

const MessdienstDashboard = ({ selectedProperty, onNavigate, supabaseClient }) => {
  // ====================================
  // STATE MANAGEMENT
  // ====================================
  
  const [viewMode, setViewMode] = useState('cards'); // 'cards' oder 'table'
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  
  // Daten-States
  const [dashboardStats, setDashboardStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [providerConfigs, setProviderConfigs] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  
  // Filter-States
  const [filters, setFilters] = useState({
    provider: '',
    deviceStatus: '',
    meterKind: ''
  });
  
  // Modal-States
  const [showProviderConfig, setShowProviderConfig] = useState(false);
  const [showServiceOrder, setShowServiceOrder] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Service initialisieren
  const meterService = new UniversalMeterService(supabaseClient);

  // ====================================
  // DATA LOADING
  // ====================================

  useEffect(() => {
    loadDashboardData();
  }, [selectedProperty]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[MESSDIENST] Loading dashboard data...');
      
      // Parallel laden für bessere Performance
      const [statsResult, devicesResult, configsResult, ordersResult, historyResult] = await Promise.all([
        meterService.getDashboardStats(selectedProperty?.id),
        meterService.getDeviceOverview(selectedProperty?.id, filters),
        selectedProperty ? meterService.getProviderConfigs(selectedProperty.id) : Promise.resolve({ success: true, configs: [] }),
        meterService.getServiceOrders(selectedProperty?.id),
        selectedProperty ? meterService.getSyncHistory(selectedProperty.id) : Promise.resolve({ success: true, history: [] })
      ]);

      console.log('[MESSDIENST] Data loaded:', {
        stats: statsResult.success,
        devices: devicesResult.success,
        configs: configsResult.success,
        orders: ordersResult.success,
        history: historyResult.success
      });

      setDashboardStats(statsResult);
      setDevices(devicesResult.devices || []);
      setProviderConfigs(configsResult.configs || []);
      setServiceOrders(ordersResult.orders || []);
      setSyncHistory(historyResult.history || []);
      
    } catch (error) {
      console.error('[MESSDIENST] Loading error:', error);
      setError('Fehler beim Laden der Messdienst-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedProperty) {
      alert('Bitte wählen Sie eine Immobilie aus');
      return;
    }

    setSyncing(true);
    try {
      const result = await meterService.triggerProviderSync(selectedProperty.id);
      
      if (result.success) {
        alert('Synchronisation erfolgreich gestartet');
        await loadDashboardData(); // Daten neu laden
      } else {
        alert(`Synchronisation fehlgeschlagen: ${result.error}`);
      }
    } catch (error) {
      console.error('[MESSDIENST] Sync error:', error);
      alert('Fehler bei der Synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  // ====================================
  // RENDER FUNCTIONS
  // ====================================

  const renderStatistikKarten = () => {
    if (!dashboardStats) return null;

    const { stats } = dashboardStats;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Gesamte Zähler */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gesamte Zähler</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {stats.totalDevices}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            <span className="text-green-600 dark:text-green-400">
              {stats.techemDevices} Techem
            </span>
            {stats.istaDevices > 0 && (
              <span className="ml-2 text-blue-600 dark:text-blue-400">
                {stats.istaDevices} ISTA
              </span>
            )}
            {stats.manualDevices > 0 && (
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {stats.manualDevices} Manual
              </span>
            )}
          </div>
        </div>

        {/* Aktive Zähler */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aktive Zähler</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                {stats.activeDevices}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {stats.warningDevices > 0 && (
              <span className="text-yellow-600 dark:text-yellow-400">
                {stats.warningDevices} Warnungen
              </span>
            )}
            {stats.errorDevices > 0 && (
              <span className="ml-2 text-red-600 dark:text-red-400">
                {stats.errorDevices} Fehler
              </span>
            )}
          </div>
        </div>

        {/* Letzte Ablesungen */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Letzte Ablesungen</p>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                {stats.recentReadings}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            <span>Letzte 7 Tage</span>
            {stats.monthlyReadings > 0 && (
              <span className="ml-2">
                ({stats.monthlyReadings} diesen Monat)
              </span>
            )}
          </div>
        </div>

        {/* Wartungen */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Wartungen</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                {stats.overdueMaintenance + stats.upcomingMaintenance}
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {stats.overdueMaintenance > 0 && (
              <span className="text-red-600 dark:text-red-400">
                {stats.overdueMaintenance} überfällig
              </span>
            )}
            {stats.upcomingMaintenance > 0 && (
              <span className={stats.overdueMaintenance > 0 ? "ml-2 text-yellow-600 dark:text-yellow-400" : "text-yellow-600 dark:text-yellow-400"}>
                {stats.upcomingMaintenance} anstehend
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActionButtons = () => (
    <div className="flex flex-wrap gap-4 mb-6">
      <button
        onClick={() => setShowProviderConfig(true)}
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-4 h-4 mr-2" />
        Zähler hinzufügen
      </button>
      
      <button
        onClick={() => alert('CSV Import wird implementiert')}
        className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
      >
        <Upload className="w-4 h-4 mr-2" />
        CSV Import
      </button>
      
      <button
        onClick={handleSync}
        disabled={syncing || !selectedProperty}
        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {syncing ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <RefreshCw className="w-4 h-4 mr-2" />
        )}
        {syncing ? 'Synchronisiere...' : 'Sync'}
      </button>

      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={() => setViewMode('cards')}
          className={`p-2 rounded-lg transition-colors ${
            viewMode === 'cards' 
              ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <Grid3X3 className="w-4 h-4" />
        </button>
        <button
          onClick={() => setViewMode('table')}
          className={`p-2 rounded-lg transition-colors ${
            viewMode === 'table' 
              ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <List className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderDeviceCard = (device) => {
    const getIcon = () => {
      switch (device.meter_kind) {
        case 'heating': return <Thermometer className="w-5 h-5" />;
        case 'electricity': return <Zap className="w-5 h-5" />;
        case 'hot_water':
        case 'cold_water': return <Droplets className="w-5 h-5" />;
        case 'gas': return <Flame className="w-5 h-5" />;
        default: return <BarChart3 className="w-5 h-5" />;
      }
    };

    const getStatusColor = () => {
      switch (device.device_status) {
        case 'active': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
        case 'warning': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
        case 'error': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
        default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900';
      }
    };

    const getProviderColor = () => {
      switch (device.provider_name) {
        case 'techem': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
        case 'ista': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
        case 'brunata': return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900';
        default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900';
      }
    };

    return (
      <div key={device.id} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg mr-3">
              {getIcon()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {device.external_id}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {device.manufacturer} {device.model}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor()}`}>
              {device.statusBadge?.text || 'OK'}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getProviderColor()}`}>
              {device.providerInfo?.name || device.provider_name}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Letzter Stand:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {device.formattedLastReading || device.last_reading_value || '-'}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Ablesedatum:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {device.formattedLastReadingDate || '-'}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Immobilie:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {device.property_name || '-'}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Einheit:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {device.unit_identifier || '-'}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Standort:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {device.location_description || '-'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => alert('Ablesung wird implementiert')}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Ablesung
          </button>
          
          <button
            onClick={() => {
              setSelectedDevice(device);
              setShowServiceOrder(true);
            }}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Settings className="w-4 h-4 mr-2" />
            Bearbeiten
          </button>
        </div>
      </div>
    );
  };

  const renderDeviceTable = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Zähler
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Provider
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Letzter Stand
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Standort
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {devices.map((device) => (
              <tr key={device.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg mr-3">
                      {device.icon}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {device.external_id}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {device.manufacturer} {device.model}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    device.provider_name === 'techem' ? 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900' :
                    device.provider_name === 'ista' ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900' :
                    'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
                  }`}>
                    {device.providerInfo?.name || device.provider_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    device.device_status === 'active' ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900' :
                    device.device_status === 'warning' ? 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900' :
                    'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
                  }`}>
                    {device.statusBadge?.text || 'OK'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <div>{device.formattedLastReading || device.last_reading_value || '-'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {device.formattedLastReadingDate || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <div>{device.property_name || '-'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {device.unit_identifier || '-'} • {device.location_description || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => alert('Ablesung wird implementiert')}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Ablesung
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDevice(device);
                        setShowServiceOrder(true);
                      }}
                      className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                      Bearbeiten
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSyncStatus = () => {
    if (!selectedProperty || syncHistory.length === 0) return null;

    const lastSync = syncHistory[0];
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg mr-3 ${
              lastSync.sync_status === 'success' ? 'bg-green-100 dark:bg-green-900' :
              lastSync.sync_status === 'error' ? 'bg-red-100 dark:bg-red-900' :
              'bg-yellow-100 dark:bg-yellow-900'
            }`}>
              {lastSync.sync_status === 'success' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : lastSync.sync_status === 'error' ? (
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Letzte Synchronisation: {lastSync.formattedDate}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {lastSync.provider_name} • {lastSync.durationFormatted} • {lastSync.records_processed} Datensätze
              </p>
            </div>
          </div>
          
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            lastSync.sync_status === 'success' ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900' :
            lastSync.sync_status === 'error' ? 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900' :
            'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
          }`}>
            {lastSync.statusBadge?.text || lastSync.sync_status}
          </span>
        </div>
      </div>
    );
  };

  // ====================================
  // MAIN RENDER
  // ====================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600 dark:text-gray-400">Lade Messdienst-Daten...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
        <button
          onClick={loadDashboardData}
          className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Digitaler Messdienst
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Verbrauchserfassung und Heizkostenabrechnung
          </p>
        </div>
        
        {dashboardStats && !dashboardStats.success && (
          <div className="text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900 px-3 py-1 rounded-lg">
            Demo-Modus
          </div>
        )}
      </div>

      {/* Sync Status */}
      {renderSyncStatus()}

      {/* Statistik-Karten */}
      {renderStatistikKarten()}

      {/* Action Buttons */}
      {renderActionButtons()}

      {/* Geräte-Ansicht */}
      {devices.length === 0 ? (
        <div className="text-center py-12">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Keine Zähler gefunden
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Fügen Sie Ihren ersten Zähler hinzu oder importieren Sie Daten aus einer CSV-Datei.
          </p>
          <button
            onClick={() => setShowProviderConfig(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ersten Zähler hinzufügen
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {devices.map(renderDeviceCard)}
            </div>
          ) : (
            renderDeviceTable()
          )}
        </>
      )}

      {/* Modals würden hier implementiert werden */}
      {showProviderConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Provider-Konfiguration
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Diese Funktion wird implementiert, sobald Sie die API-Keys bereitstellen.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowProviderConfig(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {showServiceOrder && selectedDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Service-Auftrag für {selectedDevice.external_id}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Service-Aufträge werden implementiert, sobald die Provider-APIs verfügbar sind.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowServiceOrder(false);
                  setSelectedDevice(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessdienstDashboard;

